import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { route } from '../../../../../constants/routes';
import { useAddJobValidation } from '../../../../../hooks/useAddJobValidation';
import { useProfile } from '../../profile/components/ProfileContext';
import { DashboardSnapshot } from '../models/dashboard.model';
import { DashboardService } from '../services/dashboard.service';
import {
  buildDashboardActivities,
  countScheduledInterviews,
  getDashboardGreeting,
} from '../utils/dashboard.utils';
import DailyActivitiesPanel from './DailyActivitiesPanel';
import DashboardStats from './DashboardStats';
import PipelineOverview from './PipelineOverview';
import RecentApplicationsPanel from './RecentApplicationsPanel';
import RecentClarificationsPanel from './RecentClarificationsPanel';
import './Dashboard.css';

type LoadState = 'loading' | 'ready' | 'error';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { profile, companyId } = useProfile();
  const { handleAddJob, checking } = useAddJobValidation();
  const [reference] = useState<Dayjs>(() => dayjs());
  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;
    const loadDashboard = async () => {
      setLoadState('loading');
      try {
        const data = await DashboardService.getSnapshot(companyId, reference);
        if (!cancelled) {
          setSnapshot(data);
          setLoadState('ready');
        }
      } catch {
        if (!cancelled) {
          setLoadState('error');
        }
      }
    };

    void loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [companyId, reference, refreshKey]);

  const activities = useMemo(
    () =>
      buildDashboardActivities(
        snapshot?.interviews ?? [],
        snapshot?.followUpApplications ?? [],
        reference
      ),
    [reference, snapshot?.followUpApplications, snapshot?.interviews]
  );

  const openApplication = useCallback(
    (applicationId: string) => {
      navigate(route(`/job-applications/${applicationId}`), {
        state: { fromDashboard: true },
      });
    },
    [navigate]
  );

  const openClarification = useCallback(
    (applicationId: string, requestId: string) => {
      navigate(
        route(
          `/job-applications/${applicationId}?tab=clarifications&clarification=${requestId}`
        ),
        { state: { fromDashboard: true } }
      );
    },
    [navigate]
  );

  const firstName =
    profile?.firstname?.trim() ||
    profile?.fullname?.trim().split(/\s+/)[0] ||
    'there';
  const unavailableSections =
    snapshot?.partialErrors.map((error) => error.sectionLabel) ?? [];
  const partialErrorMessage =
    unavailableSections.length === 1
      ? `${unavailableSections[0]} is temporarily unavailable`
      : `${unavailableSections.length} Dashboard sections are temporarily unavailable`;
  const partialErrorDescription =
    unavailableSections.length === 1
      ? 'The rest of the Dashboard is current. Refresh to try this section again.'
      : `Unavailable: ${unavailableSections.join(
          ', '
        )}. The available sections are shown below.`;

  return (
    <main className="dashboard-premium">
      <header className="dashboard-hero">
        <div>
          <span className="dashboard-hero__kicker">Talent operations</span>
          <h1>
            {getDashboardGreeting(reference.hour())}, {firstName}
          </h1>
          <p>Here’s what’s happening across your hiring pipeline today.</p>
        </div>
        <button
          className="dashboard-primary-action"
          type="button"
          disabled={checking}
          onClick={() => void handleAddJob()}
        >
          {checking ? (
            <ReloadOutlined spin aria-hidden />
          ) : (
            <PlusOutlined aria-hidden />
          )}
          {checking ? 'Checking clients…' : 'Create job'}
        </button>
      </header>

      {loadState === 'error' ? (
        <Alert
          className="dashboard-alert"
          type="error"
          showIcon
          message="We couldn’t load the Dashboard"
          description="Check your connection and try again."
          action={
            <button
              className="dashboard-secondary-action"
              type="button"
              onClick={() => setRefreshKey((current) => current + 1)}
            >
              Try again
            </button>
          }
        />
      ) : null}

      {loadState === 'loading' || !snapshot ? (
        <div
          className="dashboard-skeleton"
          aria-busy="true"
          aria-label="Loading Dashboard"
        >
          <div className="dashboard-skeleton__stats" />
          <div className="dashboard-skeleton__content">
            <div />
            <div />
          </div>
          <div className="dashboard-skeleton__pipeline" />
        </div>
      ) : (
        <>
          {snapshot.hasPartialError ? (
            <Alert
              className="dashboard-alert"
              type="warning"
              showIcon
              message={partialErrorMessage}
              description={partialErrorDescription}
              action={
                <button
                  className="dashboard-secondary-action"
                  type="button"
                  onClick={() => setRefreshKey((current) => current + 1)}
                >
                  Refresh
                </button>
              }
            />
          ) : null}

          <DashboardStats
            openJobs={snapshot.openJobs}
            activeCandidates={snapshot.activeCandidates}
            interviewsThisWeek={countScheduledInterviews(snapshot.interviews)}
            followUpCount={snapshot.followUpCount}
          />

          <div className="dashboard-main-grid">
            <RecentApplicationsPanel
              applications={snapshot.recentApplications}
              onOpenApplication={openApplication}
              onViewAll={() => navigate(route('/job-applications'))}
            />
            <DailyActivitiesPanel
              activities={activities}
              dateLabel={reference.format('dddd, MMMM D')}
              onOpenActivity={openApplication}
              onViewAgenda={() => navigate(route('/interviews/agenda'))}
            />
          </div>

          <RecentClarificationsPanel
            requests={snapshot.recentClarifications ?? []}
            onOpenApplication={openClarification}
            onViewAll={() =>
              navigate(route('/job-applications'), {
                state: { clarificationFocus: true },
              })
            }
          />

          <PipelineOverview
            activeCandidates={snapshot.activeCandidates}
            openJobs={snapshot.openJobs}
            stages={snapshot.pipeline}
            onViewPipeline={() =>
              navigate(route('/job-applications'), {
                state: { viewMode: 'board' },
              })
            }
          />
        </>
      )}
    </main>
  );
};

export default Dashboard;
